import { useCallback, useEffect, useRef, useState } from 'react';
import {
  describeFeedbackError,
  listFeedback,
  setFeedbackStatus,
  type FeedbackReport,
  type FeedbackStatus,
} from '../../services/feedback-api';

/**
 * HQ Feedback Owner inbox. Mounted ONLY for the HQ Owner (a UI hint) and only while its tab is showing, so nobody else triggers an
 * inbox call and nothing polls while the inbox is not on screen. The screen is a convenience: the Feedback function enforces Owner
 * on every list and status call.
 *
 * - Open reports first, newest first; a segmented control shows Completed reports (and Reopen).
 * - Refreshes when opened and every 30 s while the browser tab is visible (paused when hidden); the Refresh button always works.
 * - A failed load is shown as an error, never as "no reports".
 * - Report text is rendered as plain text (React escapes it); it is never injected as HTML.
 * - There is no delete.
 */

export const INBOX_POLL_MS = 30_000;

function Chevron({ down }: { down: boolean }) {
  return (
    <svg className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {down ? <path d="m6 9 6 6 6-6" /> : <path d="m9 6 6 6-6 6" />}
    </svg>
  );
}
function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg className={`w-3 h-3 ${spinning ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />
    </svg>
  );
}

/**
 * Runs `refresh` immediately when enabled, then every `intervalMs` - but only while the browser tab is visible. Hidden tab: the
 * timer stops; visible again: refresh at once and restart. A change of `restartKey` (for example the selected filter) restarts it,
 * which refreshes at once.
 */
function useVisiblePolling(refresh: () => void | Promise<void>, enabled: boolean, intervalMs: number, restartKey?: unknown): void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;
    let timer: ReturnType<typeof setInterval> | null = null;
    const isVisible = () => document.visibilityState === 'visible';
    const run = () => { void refreshRef.current(); };
    const start = () => { if (timer === null) timer = setInterval(run, intervalMs); };
    const stop = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
    const onVisibility = () => { if (isVisible()) { run(); start(); } else { stop(); } };
    if (isVisible()) { run(); start(); }
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); stop(); };
  }, [enabled, intervalMs, restartKey]);
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function firstLine(text: string): string {
  const line = text.split('\n').find((l) => l.trim().length > 0) ?? '';
  return line.length > 90 ? `${line.slice(0, 90)}…` : line;
}

export function FeedbackInbox() {
  const [filter, setFilter] = useState<FeedbackStatus>('open');
  const [items, setItems] = useState<FeedbackReport[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await listFeedback({ status: filter });
      if (mine !== seq.current) return; // a newer request (or a filter change) superseded this one
      setItems(res.items);
      setNextToken(res.nextToken);
      setError(null);
      setLoadedOnce(true);
      setUpdatedAt(new Date());
    } catch (e) {
      if (mine !== seq.current) return;
      setError(describeFeedbackError(e));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [filter]);

  useVisiblePolling(refresh, true, INBOX_POLL_MS, filter);

  function chooseFilter(next: FeedbackStatus) {
    if (next === filter) return;
    seq.current += 1; // drop anything still in flight for the previous filter
    setFilter(next);
    setItems([]);
    setNextToken(null);
    setLoadedOnce(false);
    setError(null);
    setExpandedId(null);
    setActionError(null);
  }

  async function loadMore() {
    if (!nextToken || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const res = await listFeedback({ status: filter, nextToken });
      if (mine !== seq.current) return;
      setItems((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...res.items.filter((r) => !seen.has(r.id))];
      });
      setNextToken(res.nextToken);
      setError(null);
    } catch (e) {
      if (mine === seq.current) setError(describeFeedbackError(e));
    } finally {
      setLoadingMore(false);
    }
  }

  async function changeStatus(report: FeedbackReport, target: FeedbackStatus) {
    if (actingId) return;
    setActingId(report.id);
    setActionError(null);
    try {
      const res = await setFeedbackStatus(report.id, target);
      // The report now belongs to the other list: take it out of this one.
      setItems((prev) => (res.report.status === filter ? prev.map((r) => (r.id === report.id ? res.report : r)) : prev.filter((r) => r.id !== report.id)));
      if (res.report.status !== filter) setExpandedId((cur) => (cur === report.id ? null : cur));
    } catch (e) {
      setActionError({ id: report.id, message: describeFeedbackError(e) });
    } finally {
      setActingId(null);
    }
  }

  const showEmpty = loadedOnce && !error && items.length === 0;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="feedback-inbox">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-200">
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group" aria-label="Report status">
          {(['open', 'completed'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={filter === s}
              onClick={() => chooseFilter(s)}
              data-testid={`feedback-filter-${s}`}
              className={`px-3 py-1 text-xs font-medium ${filter === s ? 'bg-[#12223f] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {s === 'open' ? 'Open' : 'Completed'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          {updatedAt && <span data-testid="feedback-updated">Updated {updatedAt.toLocaleTimeString()}</span>}
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            aria-label="Refresh reports"
          >
            <RefreshIcon spinning={loading} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="feedback-inbox-error">
          {loadedOnce ? 'Could not refresh. Showing the last list that loaded. ' : 'Could not load reports. '}
          {error}{' '}
          <button type="button" className="underline font-medium" onClick={() => void refresh()}>Try again</button>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {!loadedOnce && !error && <p className="text-xs text-gray-500" role="status">Loading reports…</p>}
        {showEmpty && (
          <p className="text-xs text-gray-500" role="status" data-testid="feedback-empty">
            {filter === 'open' ? 'No open reports.' : 'No completed reports yet.'}
          </p>
        )}
        <ul className="space-y-2">
          {items.map((r) => {
            const open = expandedId === r.id;
            const panelId = `feedback-report-${r.id}`;
            return (
              <li key={r.id} className="rounded-md border border-gray-200 bg-white" data-testid="feedback-item" data-status={r.status}>
                <button
                  type="button"
                  className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => setExpandedId(open ? null : r.id)}
                >
                  <Chevron down={open} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${r.type === 'Bug' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{r.type}</span>
                      <span className="text-[11px] text-gray-500 truncate">{r.submittedBy.email || r.submittedBy.name || r.submittedBy.userId}</span>
                      <span className="text-[11px] text-gray-400 ml-auto flex-shrink-0">{when(r.createdAt)}</span>
                    </span>
                    <span className="block text-xs text-gray-800 mt-1 truncate">{firstLine(r.details)}</span>
                  </span>
                </button>
                {open && (
                  <div id={panelId} className="border-t border-gray-100 px-3 py-2 space-y-2">
                    <p className="text-xs text-gray-900 whitespace-pre-wrap break-words" data-testid="feedback-details">{r.details}</p>
                    <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
                      <dt className="text-gray-400">Reference</dt><dd>{r.reference}</dd>
                      <dt className="text-gray-400">From</dt><dd className="break-all">{r.submittedBy.name ? `${r.submittedBy.name} · ` : ''}{r.submittedBy.email || r.submittedBy.userId} ({r.submittedBy.role || 'unknown role'})</dd>
                      <dt className="text-gray-400">Page</dt><dd className="break-all">{r.pageContext?.path || 'not recorded'}</dd>
                      <dt className="text-gray-400">Sent</dt><dd>{when(r.createdAt)}</dd>
                    </dl>
                    {r.statusChangedAt && (
                      <p className="text-[11px] text-gray-500" data-testid="feedback-status-note">
                        {r.status === 'completed' ? 'Completed' : 'Reopened'} · {when(r.statusChangedAt)}{r.statusChangedBy?.name ? ` · ${r.statusChangedBy.name}` : ''}
                      </p>
                    )}
                    {actionError?.id === r.id && <p role="alert" className="text-xs text-red-700">{actionError.message}</p>}
                    <div>
                      {r.status === 'open' ? (
                        <button
                          type="button"
                          onClick={() => void changeStatus(r, 'completed')}
                          disabled={actingId !== null}
                          className="px-3 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {actingId === r.id ? 'Saving…' : 'Mark complete'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void changeStatus(r, 'open')}
                          disabled={actingId !== null}
                          className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                        >
                          {actingId === r.id ? 'Saving…' : 'Reopen'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {nextToken && (
          <div className="pt-3">
            <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
